import React, { useEffect, useRef, useState } from 'react';

/**
 * The motion vocabulary the site uses outside the front-page story.
 *
 * Three rules hold for everything here. It respects `prefers-reduced-motion`,
 * and not by shortening the animation — by not animating at all, with the
 * content in its final state. It never animates anything a reader needs before
 * the animation finishes, so a failed observer or a stalled frame loop leaves
 * a usable page rather than an empty one. And it moves only `transform` and
 * `opacity`, which the compositor can do without laying the page out again.
 */

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduced(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);
  return reduced;
}

/**
 * Fades and lifts its children into place the first time they come into view.
 *
 * Starts visible and only becomes hidden once the observer is attached, so a
 * browser without IntersectionObserver — or one where the effect never runs —
 * shows the content rather than a blank column.
 */
export const Reveal: React.FC<{
  children: React.ReactNode;
  /** Seconds to wait after the element appears, for staggering a group. */
  delay?: number;
  /** How far it travels, in pixels. Larger reads as heavier. */
  distance?: number;
  className?: string;
}> = ({ children, delay = 0, distance = 18, className = '' }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(true);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced || typeof IntersectionObserver === 'undefined') return;
    const node = ref.current;
    if (!node) return;
    // Only now is it safe to hide it: the thing that will show it again exists.
    setShown(false);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          setShown(true);
          observer.disconnect();
        }
      },
      // A little before the edge, so it is already settling as it arrives
      // rather than starting once the reader is looking straight at it.
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [reduced]);

  return (
    <div
      ref={ref}
      className={className}
      style={reduced ? undefined : {
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : `translate3d(0, ${distance}px, 0)`,
        transition: `opacity 620ms cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}s, transform 620ms cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}s`,
        willChange: shown ? undefined : 'transform, opacity'
      }}
    >
      {children}
    </div>
  );
};

/**
 * Leans towards the pointer, which gives a flat screenshot the same depth the
 * story gives it. Pointer-driven rather than continuous: nothing runs until
 * somebody is actually over the element, and it returns to flat on leave.
 *
 * Disabled where there is no hover — on a touch screen the tilt would either
 * never fire or stick after a tap.
 */
export const Tilt: React.FC<{
  children: React.ReactNode;
  /** Maximum lean, in degrees. */
  max?: number;
  className?: string;
}> = ({ children, max = 7, className = '' }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const reduced = useReducedMotion();
  const [hoverable, setHoverable] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(hover: hover) and (pointer: fine)');
    const sync = () => setHoverable(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  const active = hoverable && !reduced;

  const lean = (event: React.PointerEvent<HTMLDivElement>) => {
    const node = ref.current;
    if (!node || !active) return;
    const box = node.getBoundingClientRect();
    const x = (event.clientX - box.left) / box.width - 0.5;
    const y = (event.clientY - box.top) / box.height - 0.5;
    node.style.transform =
      `perspective(900px) rotateY(${(x * max * 2).toFixed(2)}deg) rotateX(${(-y * max * 2).toFixed(2)}deg) translateZ(6px)`;
  };

  const flatten = () => {
    const node = ref.current;
    if (node) node.style.transform = '';
  };

  return (
    <div
      ref={ref}
      className={className}
      onPointerMove={lean}
      onPointerLeave={flatten}
      style={active ? { transition: 'transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)', willChange: 'transform' } : undefined}
    >
      {children}
    </div>
  );
};

/**
 * Counts up to a number once it is on screen.
 *
 * The final value is rendered from the first paint and only replaced while the
 * count runs, so the figure is correct even if the animation never starts —
 * these are measurements, and a number that reads 0 because a frame loop failed
 * would be worse than no animation at all.
 */
export const CountUp: React.FC<{
  value: number;
  /** Milliseconds for the whole count. */
  duration?: number;
  className?: string;
}> = ({ value, duration = 900, className = '' }) => {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [shown, setShown] = useState(value);
  const reduced = useReducedMotion();

  useEffect(() => {
    setShown(value);
    if (reduced || typeof IntersectionObserver === 'undefined' || !Number.isFinite(value)) return;
    const node = ref.current;
    if (!node) return;
    let frame = 0;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.disconnect();
        const started = performance.now();
        const tick = (now: number) => {
          const t = Math.min(1, (now - started) / duration);
          // Decelerating, so it lands rather than stops.
          const eased = 1 - (1 - t) ** 3;
          setShown(Math.round(value * eased));
          if (t < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    observer.observe(node);
    return () => { observer.disconnect(); if (frame) cancelAnimationFrame(frame); };
  }, [value, duration, reduced]);

  return <span ref={ref} className={className}>{shown}</span>;
};

/**
 * Which theme the reader is actually in.
 *
 * The screenshots and charts exist in both, and the right one depends on a
 * class the theme toggle writes on <html> — so this watches for that rather
 * than reading it once at mount and going stale on the first toggle.
 */
export function useIsDark(): boolean {
  const [isDark, setIsDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setIsDark(root.classList.contains('dark')));
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}
